import { useState, useEffect, useRef, memo } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import { PlayArrow } from '@mui/icons-material'
import { Box, Fade } from '@mui/material'

import { checkIfAnimated } from '../../utils/image'

const BLANK_CHECK_GRID = 5

/** True when every sampled pixel is fully transparent, i.e. the frame was drawn before it was decoded. */
const isBlankFrame = (ctx: CanvasRenderingContext2D, width: number, height: number): boolean => {
  for (let row = 0; row < BLANK_CHECK_GRID; row++) {
    for (let col = 0; col < BLANK_CHECK_GRID; col++) {
      const x = Math.min(width - 1, Math.floor(((col + 0.5) * width) / BLANK_CHECK_GRID))
      const y = Math.min(height - 1, Math.floor(((row + 0.5) * height) / BLANK_CHECK_GRID))
      if (ctx.getImageData(x, y, 1, 1).data[3] > 0) return false
    }
  }
  return true
}

/** Longest edge of the poster; the poster only needs to look right at card size, not at the source resolution. */
const MAX_POSTER_EDGE = 1920
const MAX_POSTER_PIXEL_RATIO = 2

/**
 * Size to draw the poster at: the on-screen size at device resolution when known, never above the source
 * or the cap. Scaled by the larger of the two axis ratios so `object-fit: cover` cards (which show the
 * image larger than their box along one axis) still get enough pixels.
 */
const getPosterSize = (image: HTMLImageElement): { width: number; height: number } => {
  const { naturalWidth, naturalHeight, clientWidth, clientHeight } = image
  const pixelRatio = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, MAX_POSTER_PIXEL_RATIO)
  const displayScale = Math.max(clientWidth / naturalWidth, clientHeight / naturalHeight) * pixelRatio
  const scale = Math.min(1, displayScale > 0 ? displayScale : 1, MAX_POSTER_EDGE / Math.max(naturalWidth, naturalHeight))
  return { width: Math.max(1, Math.round(naturalWidth * scale)), height: Math.max(1, Math.round(naturalHeight * scale)) }
}

/** Draws the current frame of the image to the canvas; false when the context is unavailable or nothing was painted. */
const paintFrame = (image: HTMLImageElement, canvas: HTMLCanvasElement): boolean => {
  // The blank check reads pixels back, so keep the canvas CPU-backed instead of forcing GPU readbacks.
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return false

  const { width, height } = getPosterSize(image)
  // Assigning the size also clears the canvas, so a retry after a blank attempt starts clean.
  canvas.width = width
  canvas.height = height
  ctx.drawImage(image, 0, 0, width, height)

  return !isBlankFrame(ctx, width, height)
}

/**
 * Draws the first frame of a loaded image to a canvas and returns it as a PNG blob, or null when
 * that isn't possible: decode rejected, tainted canvas, drawing/encoding threw, the frame is empty,
 * or the caller gave up (`isCancelled`) while waiting for the decode.
 *
 * The frame is drawn straight away: drawing only needs the first frame, which every browser can produce
 * quickly. Only when that comes out blank (Safari can report an image as loaded before its first frame
 * is decoded) do we wait for `decode()`, which on a large animated image can take a long time. The caller
 * keeps the live image visible meanwhile, so waiting never leaves an empty card.
 */
const extractFirstFrame = async (image: HTMLImageElement, canvas: HTMLCanvasElement | null, isCancelled: () => boolean): Promise<Blob | null> => {
  try {
    if (!canvas || !image.naturalWidth || !image.naturalHeight) return null

    let painted = paintFrame(image, canvas)
    if (!painted && typeof image.decode === 'function') {
      await image.decode()
      if (isCancelled()) return null
      painted = paintFrame(image, canvas)
    }
    if (!painted) return null

    return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  } catch {
    return null
  } finally {
    // The canvas is only a scratch buffer; release its backing store (iOS Safari caps total canvas memory).
    if (canvas) {
      canvas.width = 0
      canvas.height = 0
    }
  }
}

export type AnimatedImageProps = {
  /** URL of the image to display */
  url?: string
  /** Alt text for the image */
  alt?: string
  /** Additional CSS class names */
  className?: string
  /** Callback when image loads */
  onLoad?: () => void
  /** Callback when image fails to load */
  onError?: () => void
  /** Callback when image is clicked */
  onClick?: () => void
  /** Whether to show play icon on animatable images (default: true) */
  showPlayIcon?: boolean
  /** Custom play icon size (default: 48) */
  playIconSize?: number
  /** Whether to loop the animation (default: true) - reserved for future use */
  loop?: boolean
  /** Whether to automatically play animations or only on hover (default: false - hover only) */
  autoPlay?: boolean
  /** External control for playing animation (overrides internal hover state when provided) */
  isPlaying?: boolean
  /** Custom image style */
  imgStyle?: React.CSSProperties
  /**
   * Whether to defer loading until the image scrolls into view (default: true).
   * Turn off when the parent already mounts the image on demand (e.g. inside a collapsed
   * section), where an IntersectionObserver watching a zero-height, clipped target is redundant.
   */
  lazy?: boolean
}

/**
 * A component that displays images with support for animated formats (GIF, WebP).
 * For animated images, shows a play icon overlay and plays animation on hover.
 * The image will cover the entire container while maintaining aspect ratio.
 */
export const AnimatedImage: React.FC<AnimatedImageProps> = memo(
  ({ url, alt = '', className = '', onLoad, onError, onClick, showPlayIcon = true, playIconSize = 48, autoPlay = false, isPlaying, imgStyle, lazy = true }) => {
    const [isAnimatable, setIsAnimatable] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const [imageLoaded, setImageLoaded] = useState(false)
    const [staticUrl, setStaticUrl] = useState<string | null>(null)
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
      if (url) {
        // Reset state when URL changes
        setIsAnimatable(false)
        setImageLoaded(false)
        setStaticUrl(null)

        // Check if image is animatable
        let cancelled = false
        checkIfAnimated(url).then((animated) => {
          if (!cancelled) setIsAnimatable(animated)
        })

        return () => {
          cancelled = true
        }
      }
    }, [url])

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
      const parent = event.currentTarget.parentElement
      if (parent) parent.style.display = 'block'
      setImageElement(event.currentTarget)
      setImageLoaded(true)
      onLoad?.()
    }

    // Extract the first frame of animated images. The live <img> is only hidden once a usable
    // frame exists; on any failure it stays visible, so the worst case is an animated image
    // instead of an empty card.
    useEffect(() => {
      // Skip frame extraction in Firefox
      const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)
      if (isFirefox || !imageLoaded || !isAnimatable || !imageElement) return

      let cancelled = false
      extractFirstFrame(imageElement, canvasRef.current, () => cancelled).then((blob) => {
        if (cancelled || !blob) return
        setStaticUrl(URL.createObjectURL(blob))
      })

      return () => {
        cancelled = true
      }
    }, [isAnimatable, imageLoaded, imageElement])

    // Clean up blob URL when component unmounts
    useEffect(() => {
      return () => {
        if (staticUrl) {
          URL.revokeObjectURL(staticUrl)
        }
      }
    }, [staticUrl])

    const handleMouseEnter = () => {
      setIsHovered(true)
    }

    const handleMouseLeave = () => {
      setIsHovered(false)
    }

    // Don't render anything if URL is falsy
    if (!url) {
      return null
    }

    // Determine if animation should be playing
    // If isPlaying is provided, use it; otherwise fall back to internal logic
    const shouldAnimate = isPlaying !== undefined ? isPlaying : autoPlay || isHovered

    return (
      <Box
        className={`AnimatedImage ${className}`}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Static image for non-animated or paused state */}
        {staticUrl && isAnimatable && !shouldAnimate && (
          <Box
            component="img"
            alt={alt}
            src={staticUrl}
            style={{
              ...imgStyle,
              display: 'block',
            }}
          />
        )}

        {/* Main animated image */}
        <LazyLoadImage
          alt={alt}
          src={url}
          effect={lazy ? 'blur' : undefined}
          visibleByDefault={!lazy}
          onLoad={handleImageLoad}
          onError={onError}
          crossOrigin="anonymous"
          style={{
            ...imgStyle,
            display: !isAnimatable || shouldAnimate || !staticUrl ? 'block' : 'none',
          }}
        />

        {/* Hidden canvas for extracting first frame */}
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{
            display: 'none',
          }}
        />

        {/* Play icon overlay */}
        <Fade in={isAnimatable && showPlayIcon && !shouldAnimate && imageLoaded} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: playIconSize,
                height: playIconSize,
                borderRadius: '50%',
                border: '2px dashed rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(3px)',
              }}
            >
              <PlayArrow
                sx={{
                  fontSize: playIconSize,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              />
            </Box>
          </Box>
        </Fade>
      </Box>
    )
  }
)
